import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { convertAmountToWords } from "./NumberWordHelper";

export const exportInvoiceToExcel = (
  invoiceData,
  companyName,
  docType = "INVOICE",
) => {
  if (!invoiceData || invoiceData.length === 0) {
    alert("No data available to export.");
    return;
  }

  const CURRENT_USER_OBJECT = localStorage.getItem("currentUser")
    ? JSON.parse(localStorage.getItem("currentUser"))
    : "";
  const VATPERCENTAGE = CURRENT_USER_OBJECT[0]?.vatp;

  const firstItem = invoiceData[0];
  const isVatApplied = firstItem.deliv_site === "VAT Applied";
  const isDO = docType === "DORD";
  const isSO = docType === "SORD";
  const isInvoice = !isDO && !isSO;

  const vatPercent = isVatApplied ? Number(VATPERCENTAGE || 0) : 0;
  const vatRate = vatPercent / 100;

  const companyTRN =
    CURRENT_USER_OBJECT[0]?.trn || CURRENT_USER_OBJECT[0]?.TRN || "-";

  // --- Field normalization matching DocumentPdfTemplates.js ---
  const docNo = firstItem.inv_no || firstItem.do_no || firstItem.so_no || "-";
  const docDateField =
    firstItem.inv_date || firstItem.do_date || firstItem.so_date;

  const getQty = (item) => item.do_qty || item.tr_qty || item.so_qty || 0;
  const getUnitCost = (item) => {
    if ((item.unit || "").toUpperCase() === "FOC") return 0;
    return (
      item.unit_cost ?? item.unit_ppric ?? item.rate ?? item.unit_price ?? 0
    );
  };
  const getItemCode = (item) =>
    item.do_icode || item.so_icode || item.code || item.icode || "";
  const getRef = (item) => item.batch || item.bin || "";
  const getLocn = (item) => item.do_locn || item.location || "";

  const aoa = [];

  // --- Title row ---
  if (isDO) {
    aoa.push(["DELIVERY ORDER"]);
  } else if (isSO) {
    aoa.push(["PROFORMA INVOICE"]);
  } else {
    aoa.push([isVatApplied ? "TAX INVOICE" : "COMMERCIAL INVOICE"]);
  }
  aoa.push([]);

  // TRN row (invoice only)
  if (isInvoice && isVatApplied) {
    aoa.push([`TRN NO : ${companyTRN}`]);
    aoa.push([]);
  }

  // Client info (left side)
  const rawClientName = firstItem.custref || firstItem.tr_desc || "Client Name";
  const rawAddress = [firstItem.address1, firstItem.address2]
    .filter((addr) => addr && addr.trim() !== "")
    .join(", ");

  aoa.push(["Client:", rawClientName]);
  if (rawAddress) {
    aoa.push(["Address:", rawAddress]);
  }
  aoa.push(["TEL:", firstItem.tel || "-"]);
  if (isInvoice) {
    aoa.push(["TRN:", firstItem.governor || "-"]);
  }
  aoa.push([]);

  // --- Document details (right side) ---
  if (isDO) {
    aoa.push(["", "", "", "DO NO:", docNo]);
    aoa.push([
      "",
      "",
      "",
      "DO DATE:",
      docDateField ? new Date(docDateField).toLocaleDateString("en-GB") : "-",
    ]);
  } else if (isSO) {
    aoa.push(["", "", "", "SO NO:", docNo]);
    aoa.push([
      "",
      "",
      "",
      "SO DATE:",
      docDateField ? new Date(docDateField).toLocaleDateString("en-GB") : "-",
    ]);
  } else {
    aoa.push(["", "", "", "Invoice No:", docNo]);
    aoa.push([
      "",
      "",
      "",
      "Date:",
      docDateField ? new Date(docDateField).toLocaleDateString("en-GB") : "-",
    ]);
  }

  if (firstItem.lpo_no && firstItem.lpo_no !== "0" && firstItem.lpo_no !== 0) {
    aoa.push(["", "", "", isDO ? "LPO:" : "LPO No:", firstItem.lpo_no]);
  }
  if (isInvoice && firstItem.do_data && firstItem.do_data.trim() !== "") {
    aoa.push(["", "", "", "DO No:", firstItem.do_data]);
  }
  aoa.push(["", "", "", "Salesman:", firstItem.sale_man || "-"]);
  aoa.push([]);

  // --- Table header per document type ---
  const tableHeader = [];
  if (isDO) {
    // DO: SN, REF, CODE, DESCRIPTION, LOCN, UNIT, QTY (no pricing)
    tableHeader.push("SN", "REF", "CODE", "DESCRIPTION", "LOCN", "UNIT", "QTY");
  } else if (isSO) {
    // SO: SN, CODE, DESCRIPTION, UNIT, QTY, UNIT COST, AMOUNT
    tableHeader.push(
      "SN",
      "CODE",
      "DESCRIPTION",
      "UNIT",
      "QTY",
      "UNIT COST",
      "AMOUNT",
    );
  } else {
    // Invoice: SN, Description, Unit, Qty, Unit Price, VAT%, VAT AMT, Amount
    tableHeader.push("SN", "Description");
    const hasUnitCol = invoiceData.some(
      (item) => (item.unit || item.uom || "").trim() !== "",
    );
    if (hasUnitCol) tableHeader.push("Unit");
    tableHeader.push("Qty", "Unit Price");
    if (isVatApplied) tableHeader.push("VAT%", "VAT AMT");
    tableHeader.push("Amount");
  }
  aoa.push(tableHeader);

  // --- Table body ---
  const lineItems = invoiceData.map((item, index) => {
    const qty = getQty(item);
    const unitCost = getUnitCost(item);
    const lineTotal = qty * unitCost;
    const lineVat = lineTotal * vatRate;
    const lineTotalWithVat = lineTotal + lineVat;

    if (isDO) {
      // DO row: SN, REF, CODE, DESCRIPTION, LOCN, UNIT, QTY
      return [
        index + 1,
        getRef(item),
        getItemCode(item),
        item.idesc || item.tr_desc || "",
        getLocn(item),
        item.unit || "PCS",
        qty,
      ];
    } else if (isSO) {
      // SO row: SN, CODE, DESCRIPTION, UNIT, QTY, UNIT COST, AMOUNT
      return [
        index + 1,
        getItemCode(item),
        item.idesc || item.tr_desc || "",
        item.unit || "PCS",
        qty,
        Number(unitCost.toFixed(2)),
        Number(lineTotal.toFixed(2)),
      ];
    } else {
      // Invoice row: SN, Description, Unit, Qty, Unit Price, VAT%, VAT AMT, Amount
      const hasUnitCol = invoiceData.some(
        (itm) => (itm.unit || itm.uom || "").trim() !== "",
      );
      const row = [index + 1, item.idesc || item.tr_desc || ""];
      if (hasUnitCol) row.push(item.unit || item.uom || "");
      row.push(qty);
      row.push(Number(unitCost.toFixed(2)));
      if (isVatApplied) row.push(vatPercent);
      if (isVatApplied) row.push(Number(lineVat.toFixed(2)));
      row.push(Number(lineTotalWithVat.toFixed(2)));
      return row;
    }
  });

  lineItems.forEach((row) => aoa.push(row));
  aoa.push([]);

  // --- Totals section (SO and Invoice only, not DO) ---
  if (isSO) {
    const totalAmount = filteredDataNonFOC(invoiceData).reduce(
      (sum, item) => sum + getQty(item) * getUnitCost(item),
      0,
    );
    const discount = firstItem.discount || firstItem.disc_amt || 0;
    const taxableAmount = totalAmount - discount;
    const vatAmt =
      firstItem.deliv_site === "VAT Applied" ? taxableAmount * vatRate : 0;
    const grandTotal = taxableAmount + vatAmt;

    const colCount = tableHeader.length;
    const pushRow = (label, value) => {
      const row = new Array(colCount - 2).fill("");
      row.push(label, value);
      aoa.push(row);
    };

    pushRow("Total [Excl. VAT]:", totalAmount.toFixed(2));
    if (discount > 0) pushRow("Discount:", discount.toFixed(2));
    pushRow("Taxable Amount:", taxableAmount.toFixed(2));
    if (firstItem.deliv_site === "VAT Applied") {
      pushRow(`VAT (${vatPercent}%):`, vatAmt.toFixed(2));
    }
    pushRow(`Net Total:`, grandTotal.toFixed(2));
    aoa.push([]);

    const amountInWords = convertAmountToWords(
      grandTotal,
      firstItem.fc || "AED",
    );
    aoa.push(["Amount in Words:", amountInWords]);
    aoa.push([]);
  } else if (isInvoice) {
    const filteredData = invoiceData.filter(
      (item) => (item.unit || item.uom || "").toUpperCase() !== "FOC",
    );
    const totalExclVat = filteredData.reduce(
      (sum, item) => sum + getUnitCost(item) * getQty(item),
      0,
    );

    const specialDiscount = firstItem.disc_amt || firstItem.fdisc_amt || 0;
    const subTotal = totalExclVat - specialDiscount;
    const roundOffValue = Number(firstItem.e) || 0;
    const advancePercent = Number(firstItem.paid_amt || 0);

    let vatAmount = 0;
    let grandTotal = 0;
    let advanceReceived = 0;

    if (advancePercent > 0) {
      advanceReceived = subTotal * (advancePercent / 100);
      vatAmount = isVatApplied ? advanceReceived * vatRate : 0;
      grandTotal = advanceReceived + vatAmount + roundOffValue;
    } else {
      vatAmount = isVatApplied ? subTotal * vatRate : 0;
      grandTotal =
        Number(subTotal?.toFixed(2)) +
        Number(vatAmount?.toFixed(2)) +
        Number(roundOffValue?.toFixed(2));
    }

    const amountInWords = convertAmountToWords(
      grandTotal,
      firstItem.fc || "AED",
    );

    const hasUnitCol = invoiceData.some(
      (item) => (item.unit || item.uom || "").trim() !== "",
    );
    let totalsColOffset = hasUnitCol ? 3 : 2;
    totalsColOffset += 1; // Qty
    totalsColOffset += 1; // Unit Price
    if (isVatApplied) totalsColOffset += 2;
    totalsColOffset += 1; // Amount

    const pushSummaryRow = (label, value) => {
      const row = new Array(totalsColOffset).fill("");
      row.push(label);
      row.push(
        Number(value || 0).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      );
      aoa.push(row);
    };

    pushSummaryRow("Total (Excl VAT)", totalExclVat?.toFixed(2));

    if (Number(specialDiscount) > 0) {
      pushSummaryRow("Special Discount", specialDiscount?.toFixed(2));
      pushSummaryRow("Sub Total", subTotal?.toFixed(2));
    }

    if (Number(advanceReceived) > 0) {
      pushSummaryRow(
        `Advance Amount ${advancePercent}%`,
        advanceReceived?.toFixed(2),
      );
    }

    if (isVatApplied) {
      pushSummaryRow(`VAT@${vatPercent}%`, vatAmount?.toFixed(2));
    }

    if (roundOffValue !== 0) {
      pushSummaryRow("Round Off", roundOffValue?.toFixed(2));
    }

    pushSummaryRow("GRAND TOTAL (Incl. VAT)", grandTotal?.toFixed(2));
    aoa.push([]);

    aoa.push(["Amount in Words:", amountInWords]);
    aoa.push([]);
  }

  // Remarks
  if (firstItem.terms) {
    aoa.push(["Remarks:", firstItem.terms]);
    aoa.push([]);
  }

  // Build worksheet
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Column widths
  if (isDO) {
    ws["!cols"] = [
      { wch: 6 }, // SN
      { wch: 12 }, // REF
      { wch: 15 }, // CODE
      { wch: 40 }, // DESCRIPTION
      { wch: 10 }, // LOCN
      { wch: 8 }, // UNIT
      { wch: 8 }, // QTY
    ];
  } else if (isSO) {
    ws["!cols"] = [
      { wch: 6 }, // SN
      { wch: 15 }, // CODE
      { wch: 40 }, // DESCRIPTION
      { wch: 8 }, // UNIT
      { wch: 8 }, // QTY
      { wch: 14 }, // UNIT COST
      { wch: 14 }, // AMOUNT
    ];
  } else {
    const hasUnitCol = invoiceData.some(
      (item) => (item.unit || item.uom || "").trim() !== "",
    );
    const cols = [{ wch: 6 }, { wch: 40 }]; // SN, Description
    if (hasUnitCol) cols.push({ wch: 8 }); // Unit
    cols.push({ wch: 8 }); // Qty
    cols.push({ wch: 14 }); // Unit Price
    if (isVatApplied) cols.push({ wch: 8 }); // VAT%
    if (isVatApplied) cols.push({ wch: 14 }); // VAT AMT
    cols.push({ wch: 14 }); // Amount
    ws["!cols"] = cols;
  }

  // Build workbook and download
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Data");

  const filePrefix = isDO ? "DO" : isSO ? "SO" : "Invoice";
  const fileDocNo =
    firstItem.inv_no || firstItem.do_no || firstItem.so_no || "draft";
  const fileName = `${filePrefix}_${fileDocNo}_${new Date().toISOString().slice(0, 10)}.xlsx`;

  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
  saveAs(blob, fileName);
};

// Helper: filter out FOC items
const filteredDataNonFOC = (data) =>
  data.filter((item) => (item.unit || "").toUpperCase() !== "FOC");

export const exportPurchaseInvoiceItemsToExcel = (invoiceData, companyName) => {
  if (!invoiceData || invoiceData.length === 0) {
    alert("No data available to export.");
    return;
  }

  const getQty = (item) =>
    item.tr_qty2 || item.tr_qty || item.so_qty || item.qty || 0;
  const getUnitCost = (item) => {
    if ((item.unit || "").toUpperCase() === "FOC") return 0;
    return (
      item.unit_cost ?? item.unit_ppric ?? item.rate ?? item.unit_price ?? 0
    );
  };
  const getItemCode = (item) =>
    item.do_icode || item.so_icode || item.code || item.icode || "";

  const aoa = [];
  aoa.push(["Purchase Invoice Items"]);
  aoa.push([]);

  const tableHeader = [
    "SN",
    "CODE",
    "DESCRIPTION",
    "UNIT",
    "QTY",
    // "UNIT PRICE",
  ];
  aoa.push(tableHeader);

  const lineItems = invoiceData.map((item, index) => {
    const qty = getQty(item);
    const unitCost = getUnitCost(item);
    return [
      index + 1,
      getItemCode(item),
      item.idesc || item.tr_desc || "",
      item.unit,
      qty,
      // Number(unitCost.toFixed(2)),
    ];
  });

  lineItems.forEach((row) => aoa.push(row));

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = [
    { wch: 6 }, // SN
    { wch: 15 }, // CODE
    { wch: 40 }, // DESCRIPTION
    { wch: 8 }, // UNIT
    { wch: 8 }, // QTY
    { wch: 14 }, // UNIT PRICE
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Items");

  const firstItem = invoiceData[0];
  const docNo =
    firstItem.inv_no ||
    firstItem.do_no ||
    firstItem.so_no ||
    firstItem.srv_no ||
    "draft";
  const fileName = `PI_Items_${docNo}_${new Date().toISOString().slice(0, 10)}.xlsx`;

  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
  saveAs(blob, fileName);
};
